/* eslint no-nested-ternary: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { Trans, withTranslation } from 'react-i18next';
import {
  Feed,
  Grid,
  List,
  Loader,
  Popup,
} from 'semantic-ui-react';
import _ from 'lodash';
import moment from 'moment-timezone';
import i18n from '../i18n';
import LoanAmount from './LoanAmount';
import CurrencySymbol from './CurrencySymbol';
import BOOKING_STATUSES from '../constants/bookingStatuses';
import PAYMENT_TYPES from '../constants/paymentTypes';
import PAYMENT_STATUSES from '../constants/paymentStatuses';
import { namespaces, TRANSLATION_FILES } from '../constants/translation';
import { camelToTitle } from '../utils/strings';
import { getConstantsLabel, translateModels } from '../utils/translation';
import { parseJSON } from '../utils/json';
import { serverRequestGet } from '../actions/fetchM';
import { flashErrorMessage } from '../actions/flash';
import { getFinanceSourceLabel } from '../utils/financeSource';
import { displayMoment } from '../utils/displayTime';
import { formatLegalDate, formatInstantAsDate } from './activityDateValue';
import Timestamp from './Timestamp';

// Still needed for the module-level locale; timestamps render in the viewer's display zone.
moment.locale('en-gb');

// Also exported unwrapped so tests can drive it without the translation/connect wrappers. The
// default export below is unchanged and remains what the app renders.
export class BookingActivities extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    hasAccess: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
    showAll: PropTypes.bool,
    cancellationReasons: PropTypes.shape({
      data: PropTypes.array,
    }),
    addressSettings: PropTypes.shape({
      data: PropTypes.array,
    }),
    financeSources: PropTypes.shape({
      data: PropTypes.array,
    }),
    auth: PropTypes.shape({
      company: PropTypes.shape({
        countryCode: PropTypes.string,
      }),
    }),
    t: PropTypes.func,
  }
  static mapStateToProps = state => ({
    auth: state.auth,
    appConfig: state.appConfig,
    cancellationReasons: state.cancellationReasons,
    addressSettings: state.addressSettings,
    financeSources: state.financeSources,
  })
  constructor(props) {
    super(props);
    const cancellationReasons = props.cancellationReasons ?
      [].concat(props.cancellationReasons.data) : [];
    this.state = {
      mpUserLoading: false,
      mpUserDetails: {},
      cancellationReasons,
      currentLanguage: i18n.language,
    };
    this.validation = {};
    this.loanBox = null;
    this.handleLanguageChange = this.handleLanguageChange.bind(this);
  }
  componentDidMount() {
    // Set moment locale based on current language
    const languageMap = {
      en: 'en-gb',
      vi: 'vi',
      id: 'id',
    };
    moment.locale(languageMap[i18n.language] || 'en-gb');
    // Listen for language changes
    i18n.on('languageChanged', this.handleLanguageChange);
  }
  componentDidUpdate(prevProps, prevState) {
    // Sync moment locale when language changes
    if (prevState && prevState.currentLanguage !== this.state.currentLanguage) {
      const languageMap = {
        en: 'en-gb',
        vi: 'vi',
        id: 'id',
      };
      moment.locale(languageMap[this.state.currentLanguage] || 'en-gb');
    }
  }
  componentWillUnmount() {
    // Clean up language change listener
    i18n.off('languageChanged', this.handleLanguageChange);
  }
  handleLanguageChange = () => {
    this.setState({ currentLanguage: i18n.language });
  }
  getAddressLabel = (key) => {
    const { addressSettings } = this.props;
    const as = addressSettings.data.find(a => _.camelCase(a.columnName) === key);
    if (as) {
      return as.label;
    }
    return key;
  }
  setMPUserLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ mpUserLoading: loading }, () => {
        res(loading);
      });
    });
  }
  keyToName = (key, isPP) => {
    const { t } = this.props;
    if (isPP) {
      switch (key) {
        case 'description':
        case 'spaPrice':
        case 'name':
        case 'totalPrice':
        case 'bumiPurchaserOnly':
        case 'unitSellingPrice':
        case 'customName':
        case 'isCustomPrice':
          return translateModels(t, key, 'bookingPackagePricing');
        default:
          break;
      }
    }
    switch (key) {
      case 'address1':
      case 'address2':
      case 'address3':
      case 'address4':
      case 'address5':
      case 'address6':
      case 'address7':
      case 'address8':
      case 'address9':
      case 'address10':
        return this.getAddressLabel(key);
      default:
        return translateModels(t, key, 'booking', camelToTitle(key));
    }
  }
  fetchMarketplaceUserDetails = async (userID) => {
    const { store } = this.context;
    const { t } = this.props;
    const url = `/marketplace/staffs/${userID}/`;
    try {
      await this.setMPUserLoading(true);
      const response = await serverRequestGet(
        url,
        store,
      );
      if (response.error) {
        await store.dispatch(flashErrorMessage({ message: response.message }));
        return;
      }
      await this.setState({ mpUserDetails: response.data });
      await this.setMPUserLoading(false);
    } catch (e) {
      Bugsnag.notifyException(e);
      await this.setMPUserLoading(false);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
    }
  }
  renderCancellationReason = (value) => {
    const { cancellationReasons } = this.props;

    return cancellationReasons && cancellationReasons.data &&
      cancellationReasons.data.some(x => x.id === value) ?
      cancellationReasons.data.find(x => x.id === value).name
      : value;
  }
  renderActivityMessage = (activity) => {
    const { auth, financeSources, t } = this.props;
    const countryCode = auth && auth.company && auth.company.countryCode;
    let { booking = {} } = activity; // set booking as empty object if not exist
    if (!booking) {
      // booking value can be null, which means booking: null
      // need to explicitly set it to empty object
      booking = {};
    }
    const { projectCurrency = [] } = booking.project || {};
    const activityBefore = parseJSON(activity.before);
    const activityAfter = parseJSON(activity.after);
    const afterKeys = Object.keys(activityAfter);
    const changes = [];

    const lowerCaseMsg = activity && activity.message && activity.message.toLowerCase();
    const isPackagePricings = lowerCaseMsg && (lowerCaseMsg.includes('package pricing') || lowerCaseMsg.includes('pricing package'));
    const isUnitChange = lowerCaseMsg && lowerCaseMsg.includes('unit change');

    afterKeys.forEach((element) => {
      let message;
      let after = '';
      let before = activityBefore[element];
      if (activity.before) {
        if (!_.isEmpty(activityAfter[element]) || _.isNumber(activityAfter[element])) {
          after = activityAfter[element];
        }
      }
      switch (element) {
        case 'id':
          if (!isPackagePricings && isUnitChange) {
            if (booking.oldID && booking.oldID === before) {
              message = (
                <span>
                  <Trans ns={TRANSLATION_FILES.screens}>
                    This booking is created via <span style={{ color: '#666' }}>Unit Change</span> from <Link to={`/bookings/${booking.oldID}/`}>here</Link>
                  </Trans>
                </span>
              );
            }
            if (booking.newID && booking.newID === after) {
              message = (
                <span>
                  <Trans ns={TRANSLATION_FILES.screens}>
                    A new <Link to={`/bookings/${booking.newID}/`}>booking</Link> is created via <span style={{ color: '#666' }}>Unit Change</span>
                  </Trans>
                </span>
              );
            }
          }
          break;
        case 'statusID':
          before = getConstantsLabel(t, BOOKING_STATUSES, before);
          after = getConstantsLabel(t, BOOKING_STATUSES, after);
          break;
        case 'paymentMethodID': {
          const beforeLabel = getFinanceSourceLabel(before, financeSources.data);
          const afterLabel = getFinanceSourceLabel(after, financeSources.data);
          before = t(beforeLabel, { ns: TRANSLATION_FILES.constants, defaultValue: beforeLabel });
          after = t(afterLabel, { ns: TRANSLATION_FILES.constants, defaultValue: afterLabel });
          break;
        }
        case 'spaValue':
          before = <LoanAmount projectCurrencies={projectCurrency} format="0,000,000" amount={before} prefix={<CurrencySymbol />} />;
          after = <LoanAmount projectCurrencies={projectCurrency} format="0,000,000" amount={after} prefix={<CurrencySymbol />} />;
          break;
        case 'paymentTypeID':
          before = getConstantsLabel(t, PAYMENT_TYPES, before);
          after = getConstantsLabel(t, PAYMENT_TYPES, after);
          break;
        case 'paymentStatusID':
          before = getConstantsLabel(t, PAYMENT_STATUSES, before);
          after = getConstantsLabel(t, PAYMENT_STATUSES, after);
          break;
        case 'customerID':
          message = <span style={{ color: '#666' }}>{this.keyToName(element, isPackagePricings)} {t('changed')}</span>;
          break;
        case 'isCancelPending':
          if (activity.booking.statusID === 'cancelled_developer') {
            message = <span style={{ color: '#666' }}>{t('Cancelled all pending loan applications')}</span>;
          } else {
            before = null;
            after = null;
          }
          break;
        case 'enabledHoldDelistUnit':
          message = <span style={{ color: '#666' }}>Unit is put on hold</span>;
          break;
        case 'nettSellingPrice':
          before = <LoanAmount projectCurrencies={projectCurrency} format="0,000,000" amount={before} prefix={<CurrencySymbol />} />;
          after = <LoanAmount projectCurrencies={projectCurrency} format="0,000,000" amount={after} prefix={<CurrencySymbol />} />;
          break;
        case 'amountCents':
          before = before ? <LoanAmount projectCurrencies={projectCurrency} format="0,000,000" amount={before / 100} prefix={<CurrencySymbol />} /> : null;
          after = <LoanAmount projectCurrencies={projectCurrency} format="0,000,000" amount={after / 100} prefix={<CurrencySymbol />} />;
          break;
        // Legal calendar dates: the same day for every viewer, so they keep the configured zone.
        case 'spaSigningDate':
        case 'spaStampingDate':
          before = before ? formatLegalDate(before) : null;
          after = after ? formatLegalDate(after) : null;
          break;
        // Actual instants that are merely shown without a clock: the calendar day is the viewer's.
        case 'approvedAt':
        case 'cancelledAt':
        case 'lastSoldAt':
        case 'lastBookedAt':
          // a cleared date arrives as null (a reinstated Change Unit source loses its cancelledAt,
          // MS-6573); formatted unconditionally it read "changed from … to Invalid date" instead of
          // taking the "has been removed" branch below
          before = before ? formatInstantAsDate(before, countryCode) : null;
          after = after ? formatInstantAsDate(after, countryCode) : null;
          break;
        case 'cancellationReasonID':
          before = this.renderCancellationReason(before);
          after = this.renderCancellationReason(after);
          break;
        case 'pricePerSqm':
          before = before ? <LoanAmount projectCurrencies={projectCurrency} format="0,000,000" amount={before} prefix={<CurrencySymbol />} /> : null;
          after = <LoanAmount projectCurrencies={projectCurrency} format="0,000,000" amount={after} prefix={<CurrencySymbol />} />;
          break;
        case 'frontIC':
        case 'backIC':
          message = <span style={{ color: '#666' }}>{after}</span>;
          break;
        case 'createdAt':
        case 'updatedAt':
        case 'pendingDocsAt':
        case 'pendingDocsSubmittedAt':
        case 'approvedLoIssuedAt':
        case 'successLiIssuedAt':
        case 'rejectUnqualifiedAt':
        case 'rejectBankAt':
        case 'rejectCustomerAt':
        case 'rejectCancelledAt':
        case 'rejectLoSignedOtherBankAt':
        case 'rejectDeveloperAt':
        case 'closedAt':
        case 'reservedAt':
        case 'bookedAt':
        case 'soldAt':
        case 'unsuccessfulAt':
        case 'signAt':
        case 'rejectedAt':
        case 'requestedAt':
        case 'pdfLastSentAt':
        case 'paymentAt':
        case 'leadCreatedAt':
        case 'salesPersonSignAt':
        case 'declinedPendingCancellationAt':
        case 'systemCreatedAt':
        case 'statusUpdatedAt':
          before = before ? displayMoment(before, countryCode).format('MMMM Do YYYY, h:mm a') : null;
          after = after ? displayMoment(after, countryCode).format('MMMM Do YYYY, h:mm a') : null;
          break;
        case 'loanTracker':
        case 'maUser':
        case 'maCreated':
        case 'bookingCustomerID':
        case 'assignedMaUserID':
        case 'assignedMaPreferredName':
        case 'projectBookingCustomFieldOptionID':
        case 'projectBookingCustomFieldOptionIDs':
        case 'maUserID':
        case 'maUserAffiliateID':
        case 'maUserAffiliateCompanyID':
        case 'maUserAffiliateCompanyBranchID':
        case 'signature':
        case 'salesPersonSignature':
        case 'spaTimes':
        case 'lastReservedAt':
        case 'lastCancelledDeveloperAt':
        case 'lastCancelledAvailabilityAt':
        case 'lastExpiredAt':
        case 'himsHasChanges':
        case 'systemPricePerSqm':
        case 'newBookingNoteEncoded':
          before = null;
          after = null;
          break;
        default:
          break;
      }
      if (message) {
        changes.push(message);
      } else if (before || after) {
        const keyName = this.keyToName(element, isPackagePricings);
        if (before === false && after) {
          message = (
            <Trans ns={TRANSLATION_FILES.screens}>
              <span><span style={{ color: '#666' }}>{{ keyName }}</span> has been added</span>
            </Trans>
          );
        } else if (!before) {
          message = <span><span style={{ color: '#666' }}>{keyName}</span>: <u>{after}</u></span>;
        } else if (before && !after) {
          message = (
            <Trans ns={TRANSLATION_FILES.screens}>
              <span><span style={{ color: '#666' }}>{{ keyName }}</span> has been removed</span>
            </Trans>
          );
        } else if (['spaValue', 'nettSellingPrice', 'amountCents', 'pricePerSqm'].includes(element)) {
          message = (
            <span><span style={{ color: '#666' }}>{ keyName }</span> {t('changed from', { ns: TRANSLATION_FILES.screens })} <u>{ before }</u> {t('to', { ns: TRANSLATION_FILES.screens })} <u>{ after }</u></span>
          );
        } else {
          message = (
            <Trans ns={TRANSLATION_FILES.screens}>
              <span><span style={{ color: '#666' }}>{{ keyName }}</span> changed from <u>{{ before }}</u> to <u>{{ after }}</u></span>
            </Trans>
          );
        }
        changes.push(message);
      }
    });
    return (
      <List bulleted>
        {changes.map((element, id) => (
          <List.Item key={id}>
            {element}
          </List.Item>
        ))}
      </List>
    );
  };
  renderActivityTitle = (message) => {
    const { t } = this.props;
    let title = message;
    const regexp = / *\([^)]*\) */g;
    const hasBrackets = regexp.test(title);
    if (hasBrackets) {
      const data = title.match(regexp)[0];
      title = title.replace(regexp, ' {{data}} ');
      return t(title.trim(), { data });
    }
    return t(title);
  }
  render() {
    const { data, t } = this.props;
    const { mpUserLoading, mpUserDetails } = this.state;
    return (
      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <Feed>
              {
              data && data.length > 0 ?
              data.map((activity, idx) => (
                <Feed.Event key={idx} className="activity-logs">
                  <Feed.Label icon="user" />
                  <Feed.Content>
                    <Feed.Date>
                      <Timestamp timestamp={activity.createdAt} format="MMMM Do YYYY, h:mm:ss a" />
                    </Feed.Date>
                    <Feed.Summary>
                      {this.props.hasAccess ?
                        activity.maUserAffiliateID > 0 ?
                        activity.booking.origin && activity.booking.origin.includes('marketplace') ? (
                              activity.booking.origin.includes('_') ?
                                activity.maCreatedByPreferredName :
                                <Popup
                                  on="click"
                                  trigger={
                                    <a>
                                      {activity.maCreatedByPreferredName || 'Unknown'}
                                    </a>
                                  }
                                  onOpen={() => {
                                    this.fetchMarketplaceUserDetails(activity.userID);
                                  }}
                                  size="mini"
                                  inverted
                                  position="bottom right"
                                  disabled
                                  flowing
                                >
                                  {
                                    mpUserLoading ? (
                                      <Loader active inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
                                    ) : (
                                      <span style={{ fontSize: '15px' }}>
                                        <div>{`${translateModels(t, 'fullName', 'mpUserDetails')}: ${mpUserDetails.fullName}`}</div>
                                        <div>{`${translateModels(t, 'email', 'mpUserDetails')}: ${mpUserDetails.email}`}</div>
                                        <div>{`${translateModels(t, 'mobile', 'mpUserDetails')}: ${mpUserDetails.mobilePrefix}${mpUserDetails.mobile}`}</div>
                                        {
                                          mpUserDetails.agencyName ? (
                                            <div>{`${translateModels(t, 'agencyName', 'mpUserDetails')}: ${mpUserDetails.agencyName}`}</div>
                                          ) : null
                                        }
                                        {
                                          mpUserDetails.registrationNumber ? (
                                            <div>{`${translateModels(t, 'registrationNumber', 'mpUserDetails')}: ${mpUserDetails.registrationNumber}`}</div>
                                          ) : null
                                        }
                                      </span>
                                    )
                                  }
                                </Popup>
                        ) :
                                <Link to={`/users/${activity.maUserAffiliateID}/`}>
                                  {activity.maCreatedByPreferredName || t('Unknown')}
                                </Link>
                          : ''
                        : activity.maCreatedByPreferredName
                      }
                      {` ${this.renderActivityTitle(activity.message)}`}
                      { this.props.showAll ?
                        <span>
                          &nbsp;{t('for')}&nbsp;
                          <Link to={`/bookings/${activity.bookingID}/`}>
                            {activity.bookingCode},&nbsp;
                            {activity.projectUnitReference},&nbsp;
                            {activity.projectName}
                          </Link>
                        </span>
                        : ''
                      }
                    </Feed.Summary>
                    <Feed.Extra text>
                      { activity.after && this.renderActivityMessage(activity) }
                    </Feed.Extra>
                  </Feed.Content>
                </Feed.Event>
                )) :
                this.props.loading ?
                  <br />
                : <center>{t('No activity found')}.</center>
              }
            </Feed>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default withTranslation([
  ...namespaces,
  TRANSLATION_FILES.models.booking,
  TRANSLATION_FILES.models.bookingPackagePricing,
  TRANSLATION_FILES.models.mpUserDetails,
])(connect(BookingActivities.mapStateToProps)(BookingActivities));



// WEBPACK FOOTER //
// ./src/components/BookingActivities.jsx